import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Heading,
  Link,
  Text,
  customColors,
} from '@apollo/orbit';
import { sumBy } from 'lodash';
import { Circle } from 'rc-progress';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { clientsRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { format } from 'src/lib/format';
import { UI__ClientsPageQuery_service_stats_queryStats as QueryStatsType } from 'src/lib/graphqlTypes/types';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { UNIDENTIFIED_CLIENT } from '../constants';
import { groupQueryStatsByClientVersion } from '../groupQueryStatsByClientVersion';

const formatSpecialPercentage = (ratio: number) => {
  if (ratio === 1 || ratio === 0 || (ratio < 0.99 && ratio > 0.01)) {
    return Math.round(ratio * 100);
  }
  if (ratio > 0.99) {
    return '>99';
  }
  return '<1';
};

const getQueryStatsErrorRatio = (queryStats: QueryStatsType[]) => {
  const errors = sumBy(
    queryStats,
    (clientVersionQueryStats) =>
      clientVersionQueryStats.metrics.requestsWithErrorsCount,
  );
  const requests = sumBy(
    queryStats,
    (clientVersionQueryStats) =>
      clientVersionQueryStats.metrics.totalRequestCount,
  );
  return errors / requests;
};

const Client = ({
  clientName,
  totalRequestsCount,
  queryStats,
  canViewVersions,
  selectedClientName,
  hasScrolledToClient,
  onScrollIntoView,
  hasClientNameOnPageLoad,
}: React.HTMLAttributes<HTMLDivElement> & {
  clientName: string | null | undefined;
  totalRequestsCount: number;
  queryStats: QueryStatsType[];
  canViewVersions: boolean;
  selectedClientName: string | null | undefined;
  hasScrolledToClient: boolean;
  onScrollIntoView: () => void;
  hasClientNameOnPageLoad: boolean;
}) => {
  const clientRequestsCount = sumBy(
    queryStats,
    (queryStat) => queryStat.metrics.totalRequestCount,
  );
  const errorsCount = sumBy(
    queryStats,
    (queryStat) => queryStat.metrics.requestsWithErrorsCount,
  );
  const errorRatio = clientRequestsCount && errorsCount / clientRequestsCount;

  const clientHasVersions =
    canViewVersions &&
    queryStats.some((stats) => stats.groupBy.clientVersion !== null);
  const location = useLocation();

  const UnidentifiedClientName = () => (
    <>
      Unidentified clients
      <Tooltip
        className="w-[223px]"
        interactive
        label={
          <span>
            Learn how to set client names in the{' '}
            <Link
              isExternal
              to="https://www.apollographql.com/docs/graphos/metrics/client-awareness/#setup"
            >
              docs
            </Link>
            .
          </span>
        }
      >
        <IconInfo className="text-primary" />
      </Tooltip>
    </>
  );

  const UnidentifiedClientVersion = () => (
    <span className="inline-flex items-center gap-2">
      No version reported
      <Tooltip
        interactive
        label={
          <span>
            Learn how to set versions in the{' '}
            <Link
              isExternal
              to="https://www.apollographql.com/docs/graphos/metrics/client-awareness/#setup"
            >
              docs
            </Link>
            .
          </span>
        }
      >
        <IconInfo className="text-icon-primary" />
      </Tooltip>
    </span>
  );

  return (
    <Card
      isDivided
      className="mx-8 mt-4 p-0 last:mb-8"
      id={clientName ?? UNIDENTIFIED_CLIENT}
      ref={(element) => {
        // On initial page load scroll to the selected client. Don't scroll to
        // the selected client if there was no client selected on initial page
        // load.
        if (
          element &&
          clientName === selectedClientName &&
          !hasScrolledToClient &&
          hasClientNameOnPageLoad
        ) {
          element.scrollIntoView();
          onScrollIntoView();
        }
      }}
    >
      <CardHeader>
        <CardTitle>{clientName || <UnidentifiedClientName />}</CardTitle>
      </CardHeader>
      <CardBody className="gap-0 p-0 last:pb-0">
        <NavLink
          to={clientsRouteConfig.locationFrom({
            location,
            patch: {
              clientName: clientName ?? UNIDENTIFIED_CLIENT,
              clientVersion: RouteConfig.DELETE,
            },
          })}
          isActive={(_, linkLocation) => {
            const other = clientsRouteConfig.parseParams(linkLocation);
            return (
              other.clientName === clientName &&
              other.clientVersion === undefined
            );
          }}
          activeClassName="bg-secondary border-l-4 border-l-selected"
          className="flex flex-row items-center px-6 py-2 text-primary hover:bg-secondary"
          replace={true}
        >
          <div className="mr-auto flex flex-row items-baseline">
            All versions
            {errorRatio > 0 && (
              <Badge className="ml-2 whitespace-nowrap" variant="error">
                {errorRatio < 0.1 ? '< 0.1%' : format.percent(errorRatio)}{' '}
                errors
              </Badge>
            )}
          </div>
          <div className="relative mx-4 size-10">
            <Circle
              percent={
                totalRequestsCount &&
                (clientRequestsCount / totalRequestsCount) * 100
              }
              strokeColor={customColors.border.focused}
              trailColor={customColors.border.disabled}
              strokeWidth={8}
              trailWidth={8}
            />
            <Text
              as="span"
              variant="code"
              size="sm"
              className="absolute inset-0 flex size-full items-center justify-center"
            >
              {formatSpecialPercentage(
                totalRequestsCount && clientRequestsCount / totalRequestsCount,
              )}
              %
            </Text>
          </div>
          <div className="flex flex-col items-end justify-center">
            <Text as="div" variant="code" size="2xl">
              {format.requests(clientRequestsCount)}
            </Text>
            <Text as="div" size="sm" className="text-secondary">
              requests
            </Text>
          </div>
          <IconChevronRight className="ml-4 text-icon-disabled" />
        </NavLink>
        {clientHasVersions && (
          <>
            <div className="flex flex-row border-t border-primary px-6 py-2 text-base">
              <Heading variant="title" className="w-28 grow text-left">
                Versions
              </Heading>
              <Heading variant="title" className="w-24 grow pr-6 text-right">
                Requests
              </Heading>
              <Heading variant="title" className="w-24 grow pr-6 text-right">
                % Errors
              </Heading>
              <div className="w-10 grow" />
            </div>
            {groupQueryStatsByClientVersion(queryStats).map(
              ({ clientVersion, groupedQueryStats }) => (
                <NavLink
                  key={`${clientName}@${clientVersion}`}
                  to={clientsRouteConfig.locationFrom({
                    location,
                    patch: {
                      clientName,
                      clientVersion,
                    },
                  })}
                  isActive={(_, linkLocation) => {
                    const other = clientsRouteConfig.parseParams(linkLocation);
                    return (
                      other.clientName === clientName &&
                      other.clientVersion === clientVersion
                    );
                  }}
                  activeClassName="bg-secondary border-l-4 border-l-selected"
                  className="flex h-14 flex-row items-center border-t border-primary px-6 py-2 tabular-nums hover:bg-secondary"
                  replace={true}
                >
                  <div
                    className="w-28 grow truncate text-left"
                    title={clientVersion || ''}
                  >
                    {clientVersion || <UnidentifiedClientVersion />}
                  </div>
                  <div className="w-24 grow pr-6 text-right">
                    {format.requests(
                      sumBy(
                        groupedQueryStats,
                        (clientVersionQueryStats) =>
                          clientVersionQueryStats.metrics.totalRequestCount,
                      ),
                    )}
                  </div>
                  <div className="w-24 grow pr-6 text-right">
                    {((clientVersionErrorRatio) =>
                      clientVersionErrorRatio === 0
                        ? null
                        : format.number(clientVersionErrorRatio, '0%'))(
                      getQueryStatsErrorRatio(groupedQueryStats),
                    )}
                  </div>
                  <div className="flex w-10 grow items-center justify-end">
                    <IconChevronRight className="ml-4 text-icon-disabled" />
                  </div>
                </NavLink>
              ),
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export { Client };
